import type { FormEvent } from 'react';
import { useState } from 'react';

import { Button } from '../../../cdl/Button/Button';
import { Callout } from '../../../cdl/Callout/Callout';
import {
    CollapsableCheckboxGroup,
    CollapsableCheckboxGroupSection,
} from '../../../cdl/CollapsableCheckboxGroup/CollapsableCheckboxGroup';
import { DrawerContentLayout } from '../../../cdl/Drawer/DrawerContentLayout';
import { Text } from '../../../cdl/Text/Text';
import { formatCompanyName } from '../../../common/helpers/formatCompanyName.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { useContractVesselAccessData } from '../../../contracts/common/hooks/useContractVesselAccessData';

interface ContractVesselAccessDrawerProps {
    customerGroupId?: string;
    customerIds: string[];
    excludedVesselIds: string[];
    onSubmit: (excludedVesselIds: string[]) => Promise<void> | void;
}

export const ContractVesselAccessDrawer = ({
    customerGroupId,
    customerIds,
    excludedVesselIds,
    onSubmit,
}: ContractVesselAccessDrawerProps) => {
    const { customers, vessels, isLoading, isError } = useContractVesselAccessData({
        customerGroupId,
        customerIds,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const availableVesselIds = new Set(vessels.map((vessel) => vessel.id));
    const [selectedVesselIds, setSelectedVesselIds] = useState(() =>
        excludedVesselIds.filter((vesselId) => availableVesselIds.size === 0 || availableVesselIds.has(vesselId))
    );
    const vesselsByCustomerId = vessels.reduce<Record<string, typeof vessels>>((result, vessel) => {
        result[vessel.customerId] = [...(result[vessel.customerId] ?? []), vessel];
        return result;
    }, {});
    const sections: CollapsableCheckboxGroupSection[] = customers.map((customer) => ({
        id: customer.id,
        label: formatCompanyName({ company: customer }) ?? customer.name,
        defaultExpanded: true,
        options: (vesselsByCustomerId[customer.id] ?? []).map((vessel) => ({
            value: vessel.id,
            label: vessel.name,
        })),
    }));
    const sectionsWithVessels = sections.filter((section) => section.options.length > 0);
    const visibleSelectedVesselIds = selectedVesselIds.filter((vesselId) => availableVesselIds.has(vesselId));
    const hiddenExcludedVesselIds = excludedVesselIds.filter((vesselId) => !availableVesselIds.has(vesselId));
    const initialVisibleIds = new Set(excludedVesselIds.filter((id) => availableVesselIds.has(id)));
    const hasChanges =
        visibleSelectedVesselIds.length !== initialVisibleIds.size ||
        visibleSelectedVesselIds.some((id) => !initialVisibleIds.has(id));
    const canSubmit = !isLoading && !isError;
    const isSubmitButtonLoading = !!isSubmitting;
    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!canSubmit || isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        try {
            await onSubmit([...hiddenExcludedVesselIds, ...visibleSelectedVesselIds]);
        } catch {
            return;
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form style={{ height: '100%', minHeight: 0 }} onSubmit={handleSubmit}>
            <DrawerContentLayout
                isBusy={isSubmitButtonLoading}
                hasUnsavedChanges={hasChanges}
                primaryAction={
                    <Button type="submit" emphasis="high" isLoading={isSubmitButtonLoading} disabled={!canSubmit}>
                        {translate('contracts.vesselAccess.updateAccess')}
                    </Button>
                }
            >
                <Text variant="body" color="foreground.muted">
                    {translate('contracts.vesselAccess.description')}
                </Text>

                <Text as="h2" variant="fieldLabel" fontWeight={600}>
                    {translate('contracts.vesselAccess.excludedVessels')}
                </Text>

                {isError ? (
                    <Callout description={translate('global.genericError')} variant="negative" Icon={IconInfoCircle} />
                ) : null}

                {!isLoading && !isError && sectionsWithVessels.length === 0 ? (
                    <Callout
                        description={translate('contracts.vesselAccess.noVessels')}
                        variant="accent"
                        Icon={IconInfoCircle}
                    />
                ) : null}

                {!isError && sectionsWithVessels.length > 0 ? (
                    <CollapsableCheckboxGroup
                        sections={sectionsWithVessels}
                        value={visibleSelectedVesselIds}
                        onChange={setSelectedVesselIds}
                        columns={2}
                    />
                ) : null}
            </DrawerContentLayout>
        </form>
    );
};
